import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatDuration,
  type HistoryEventOption,
  type HistoryList,
  type HistoryRow,
} from '@bingo/shared';
import { api } from '../../lib/api.js';
import { useSession } from '../../lib/useSession.js';

/**
 * /historico — as partidas da organização ativa.
 *
 * Mobile-first porque é a mesma pessoa, com o mesmo celular, que operou o
 * jogo: ela abre isto no caminho de casa para conferir quem ganhou.
 *
 * A partida **em andamento** aparece no topo com "Abrir controle" — é o
 * atalho de quem fechou a aba sem querer no meio do evento.
 */

/** Dia e hora curtos: "16/10, 20:30". */
function whenLabel(iso: string): string {
  const date = new Date(iso);
  return `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}, ${date.toLocaleTimeString(
    'pt-BR',
    { hour: '2-digit', minute: '2-digit' },
  )}`;
}

/** `datetime-local`/`date` → ISO, com o dia inteiro quando é o fim. */
function dayToIso(day: string, edge: 'start' | 'end'): string | null {
  if (!day) return null;
  const date = new Date(`${day}T${edge === 'start' ? '00:00:00' : '23:59:59.999'}`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function Historico() {
  const session = useSession();

  const [rows, setRows] = useState<HistoryRow[] | null>(null);
  const [events, setEvents] = useState<HistoryEventOption[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [eventId, setEventId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const organizationId = session.activeOrganizationId;

  const buildQuery = useCallback(
    (next?: string) => {
      const params = new URLSearchParams({ limit: '20' });
      if (eventId) params.set('eventId', eventId);

      const fromIso = dayToIso(from, 'start');
      const toIso = dayToIso(to, 'end');
      if (fromIso) params.set('from', fromIso);
      if (toIso) params.set('to', toIso);
      if (next) params.set('cursor', next);

      return params.toString();
    },
    [eventId, from, to],
  );

  const load = useCallback(() => {
    if (!organizationId) {
      setRows([]);
      return;
    }

    setRows(null);
    setError(null);

    void api<HistoryList>(`/api/history/games?${buildQuery()}`)
      .then((list) => {
        setRows(list.games);
        setCursor(list.nextCursor);
      })
      .catch(() => {
        setRows([]);
        setError('Não foi possível carregar o histórico.');
      });
  }, [organizationId, buildQuery]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!organizationId) return;
    void api<HistoryEventOption[]>('/api/history/events')
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [organizationId]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);

    try {
      const list = await api<HistoryList>(`/api/history/games?${buildQuery(cursor)}`);
      setRows((current) => [...(current ?? []), ...list.games]);
      setCursor(list.nextCursor);
    } catch {
      setError('Não foi possível carregar mais partidas.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <main className="shell">
      <header className="shell-head">
        <div>
          <h1>Histórico</h1>
          <p className="hint-text">Partidas da organização, mais recentes primeiro.</p>
        </div>
        <Link className="btn" to="/inicio">
          Meus jogos
        </Link>
      </header>

      <section className="history-filters">
        <label className="field">
          <span>Evento</span>
          <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">Todos</option>
            {events.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>

        <div className="row">
          <label className="field">
            <span>De</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span>Até</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      </section>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {rows === null ? (
        <p className="hint-text">Carregando…</p>
      ) : rows.length === 0 ? (
        <div className="empty-card">
          <p>Nenhuma partida encontrada.</p>
          <p className="hint-text">
            O histórico começa quando a primeira partida é aberta num evento.
          </p>
        </div>
      ) : (
        <ul className="history-list">
          {rows.map((row) => (
            <li
              key={row.gameId}
              className={row.status === 'EM_ANDAMENTO' ? 'history-row live' : 'history-row'}
            >
              <div className="history-main">
                <strong>{row.eventName}</strong>
                <span className="hint-text">
                  Partida {row.gameNumber} · {whenLabel(row.startedAt)}
                </span>

                <span className="history-meta">
                  {row.status === 'EM_ANDAMENTO' ? (
                    <em className="pill live-pill">em andamento</em>
                  ) : (
                    <em className="pill">finalizada</em>
                  )}
                  <span>{formatDuration(row.totalMs)}</span>
                  <span>
                    {row.roundsCount} {row.roundsCount === 1 ? 'rodada' : 'rodadas'}
                  </span>
                </span>

                {row.winnerNames.length > 0 ? (
                  <span className="history-winners">🏆 {row.winnerNames.join(', ')}</span>
                ) : (
                  <span className="hint-text">Sem ganhadores registrados.</span>
                )}
              </div>

              <div className="history-actions">
                {row.status === 'EM_ANDAMENTO' ? (
                  <Link className="btn primary" to={`/controle?eventId=${row.eventId}`}>
                    Abrir controle
                  </Link>
                ) : null}
                <Link className="btn" to={`/historico/partida/${row.gameId}`}>
                  Ver resumo
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}

      {cursor ? (
        <button
          type="button"
          className="btn block"
          onClick={() => void loadMore()}
          disabled={loadingMore}
        >
          {loadingMore ? 'Carregando…' : 'Carregar mais'}
        </button>
      ) : null}
    </main>
  );
}
